
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export const useLayoutAuth = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthEnabled, setIsAuthEnabled] = useState<boolean>(true); // Always enable auth
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    const checkSession = async () => {
      try {
        console.info("Checking auth session...");
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          setError(sessionError);
        } else if (data.session) {
          console.info("Session found for user", data.session.user.id);
          setUser(data.session.user);
        } else {
          console.info("No active session found");
        }
      } catch (err: any) {
        console.error("Auth check error:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    let subscription: { unsubscribe: () => void } | null = null;
    
    try {
      // First set up auth state change listener
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        console.info("Auth state changed:", event);
        setUser(session?.user ?? null);
      });
      
      subscription = data.subscription;
      
      // Then check current session
      checkSession();
    } catch (err: any) {
      console.error("Auth subscription error:", err);
      setError(err);
      setLoading(false);
    }
    
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await supabase.auth.signOut();
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
      navigate("/");
    } catch (error: any) {
      console.error("Sign out error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to sign out. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    isAuthEnabled,
    handleSignOut,
    error
  };
};

export default useLayoutAuth;
